import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SelfPresenceBadge } from '@/features/presence';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import {
  employeeStatusLabel,
  employeeStatusVariant,
  employmentTypeLabel,
} from '@/features/hrm/employees/constants';
import { CabinetEmptyState } from '../components/listChrome';
import { CabinetProfileForm } from '../components/CabinetProfileForm';
import { useCabinetProfile } from '../queries';
import { cabinetName, type CabinetProfile } from '../types';

function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Self-service view of the employee's own HR record: an identity header, the
 *  editable personal info (shared with the HR Employee record), and a read-only
 *  employment summary. Photo, personal email and phone are HR-managed
 *  (read-only here) — only an Admin/Founder changes them via HRM. */
export function EmployeeRecordPage() {
  const { data: profile, isPending, isError, error } = useCabinetProfile();

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    const err = error as unknown as ApiError;
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Employee Record</h1>
        <CabinetEmptyState>
          {err.status === 404
            ? 'You don’t have an employee record in this organization yet. Ask your HR team to set one up.'
            : err.message}
        </CabinetEmptyState>
      </div>
    );
  }

  const subtitle = [profile.job_title, profile.department_name].filter(Boolean).join(' · ');

  return (
    <div className="max-w-3xl space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Employee Record</h1>

      {/* Identity header */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-4">
          <Avatar className="size-16">
            {profile.photo ? <AvatarImage src={profile.photo} alt="" /> : null}
            <AvatarFallback className="text-lg">{initials(cabinetName(profile))}</AvatarFallback>
            <SelfPresenceBadge className="size-3.5" />
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-lg font-semibold">{cabinetName(profile)}</p>
              <Badge variant={employeeStatusVariant(profile.status)}>
                {employeeStatusLabel(profile.status)}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              {subtitle || employmentTypeLabel(profile.employment_type)}
            </p>
          </div>
        </CardContent>
      </Card>

      <CabinetProfileForm profile={profile} footer={<EmploymentRecord profile={profile} />} />
    </div>
  );
}

function EmploymentRecord({ profile }: { profile: CabinetProfile }) {
  const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Employment record</CardTitle>
      </CardHeader>
      <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
        <Field label="Job title">{profile.job_title || '—'}</Field>
        <Field label="Department">{profile.department_name || '—'}</Field>
        <Field label="Type">{employmentTypeLabel(profile.employment_type)}</Field>
        <Field label="Manager">{profile.manager_name || '—'}</Field>
        <Field label="Hire date">{fmtDate(profile.hire_date)}</Field>
        <Field label="Trial period end">{fmtDate(profile.trial_period_end)}</Field>
        <Field label="End date">{fmtDate(profile.end_date)}</Field>
        {profile.default_commission_pct != null ? (
          <Field label="Commission rate">{Number(profile.default_commission_pct)}%</Field>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
